// shadowQuality — how one viewer's scalability tier caps the shadow-map
// resolution an artist authored (ENG-1230).
//
// Two DIFFERENT intents meet on every shadow-casting light, and keeping them
// apart is the whole point of this module:
//
//   the light's `shadowMapSize` — what the ARTIST asked for. It lives in the
//     environment's saved look, so it is shared with the team and means the
//     same thing on every machine.
//   the scalability TIER — what THIS browser can afford. It is the existing
//     per-user setting (textureQuality.js: auto | low | medium | high | ultra,
//     local, never shared, auto-derived from the GPU and measured bandwidth).
//
// The tier CAPS the authored value; it never raises it. A weak machine is
// always protected, and a strong one never renders something heavier than the
// artist chose — so a scene can look softer on a laptop, never different in a
// way nobody asked for. (The alternative, a multiplier that can also push a 1k
// light to 2k, was considered and rejected: it makes the same scene heavier on
// exactly the machines least able to say no.)
//
// Pure, no imports from the tier module, so `node --test` can drive it and so a
// caller cannot accidentally couple the cap to texture loading.

/** No light renders below this, whatever the tier or the authored value says.
 *  Under 512 the blur (shadowRadius up to 40) has nothing left to work with and
 *  the shadow reads as a blob rather than a soft edge. */
export const SHADOW_FLOOR = 512

/** The ceiling each scalability tier puts on a shadow map. `ultra` is the
 *  biggest the inspector offers, so on ultra the authored value always wins. */
export const SHADOW_CAP_BY_TIER = Object.freeze({
  low: 512,
  medium: 1024,
  high: 2048,
  ultra: 4096,
})

/** The cap for a tier. An unknown tier (or `auto`, which the caller should have
 *  resolved first) uncaps rather than guessing low — a wrong guess downward is
 *  a silent quality regression nobody can find. */
export function shadowCapForTier(tier) {
  return SHADOW_CAP_BY_TIER[tier] ?? SHADOW_CAP_BY_TIER.ultra
}

/**
 * What a light actually renders at: the authored size, capped by the tier and
 * floored at SHADOW_FLOOR.
 *
 * A missing or junk authored size falls back to the cap rather than to a
 * hardcoded number, so a light with no opinion follows the machine.
 */
export function effectiveShadowMapSize(authored, tier) {
  const cap = shadowCapForTier(tier)
  const asked = Number.isFinite(authored) && authored > 0 ? authored : cap
  return Math.max(SHADOW_FLOOR, Math.min(asked, cap))
}

/** Is the tier actually lowering what the artist asked for? Drives the note the
 *  inspector shows: a control set to 4k while the scene renders 512 has to say
 *  so, or it reads as a dead switch. */
export function isShadowCapped(authored, tier) {
  return Number.isFinite(authored) && authored > 0
    && effectiveShadowMapSize(authored, tier) < authored
}
